import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './IndentDemandReport.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const IndentDemandReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Date validation state
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // FIXED: Helper functions to handle date/time without UTC conversion
  const parseLocalDateTime = (dateString) => {
    if (!dateString) return new Date();
    
    try {
      // Remove the 'Z' and treat as local time (IST) instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      return isNaN(date.getTime()) ? new Date() : date;
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date();
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date only:', dateString, error);
      return '';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time only:', dateString, error);
      return '';
    }
  };

  // Helper function to format closed status
  const formatClosedStatus = (closedValue) => {
    if (closedValue === true) return 'Yes';
    if (closedValue === false || closedValue === null || closedValue === undefined) return 'No';
    return 'N'; // For any other value
  };

  // Calculate TAT (Turn Around Time) in hours and minutes
  const calculateTAT = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return 'N/A';
    
    try {
      const start = parseLocalDateTime(startDateTime);
      const end = parseLocalDateTime(endDateTime);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
      
      const diffMs = end.getTime() - start.getTime();
      
      if (diffMs < 0) return 'N/A'; // End time before start time
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours === 0) {
        return `${diffMinutes}m`;
      } else {
        return `${diffHours}h ${diffMinutes}m`;
      }
    } catch (error) {
      console.error('Error calculating TAT:', startDateTime, endDateTime, error);
      return 'N/A';
    }
  };

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    localStorage.clear();
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  // Date picker styling with error (red border)
  const datePickerErrorStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dc3545',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: '#c82333',
        borderWidth: '2px',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#dc3545',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#dc3545',
    },
  };

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading System Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we load indent demand data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' }
  ];

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          // Add ALL option and set the areas
          const areasWithAll = [
            { SFAreaId: 'ALL', SFAreaName: 'ALL' },
            ...data
          ];
          setAreas(areasWithAll);
          setSelectedAreaObj(null);
          setBranches([]); // Clear branches when areas change
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
        }
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        // Use SFAreaId from the API response structure
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
        }
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // API Functions with 401 handling
  const fetchAreas = async () => {
    try {
      const response = await apiCall(`${local_url}/api/SFArea/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const areasWithAll = [
          { sfAreaId: 'ALL', sfAreaName: 'ALL' },
          ...data
        ];
        setAreas(areasWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching areas:', error);
      }
    }
  };

  const fetchBranches = async (sfAreaId) => {
    try {
      const response = await apiCall(`${local_url}/api/Company/GetBranchList/${sfAreaId}/SEGMENT/ALL`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const branchesWithAll = [
          { branchID: 'ALL', branchName: 'ALL' },
          ...data
        ];
        setBranches(branchesWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching branches:', error);
      }
    }
  };

  // FIXED: Fetch indent report data with proper error handling
  const fetchIndentReportData = async (areaId, branchId, fromDate, toDate, closedParam = 'AL') => {
    try {
      const startDate = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
      const endDate = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');

      const response = await apiCall(
        `${local_signage}/api/v1/indentreport?BizSegmentId=${branchType || 'ALL'}&AreaID=${areaId}&BranchID=${branchId}&ItemID=ALL&StartDate=${startDate}&EndDate=${endDate}&closed=AL`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Raw API Response:', data); // Debug log
        
        // Handle the new API response structure with DashBoard wrapper
        let actualData = data;
        if (data && data.DashBoard && Array.isArray(data.DashBoard)) {
          actualData = data.DashBoard;
        } else if (Array.isArray(data)) {
          actualData = data;
        } else {
          console.error('Unexpected API response format:', data);
          actualData = [];
        }
        
        console.log('Processed data for transformation:', actualData); // Debug log
        return transformApiData(actualData);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch indent report data:', response.status, errorText);
        alert(`Failed to fetch data: ${response.status} - ${errorText}`);
        return [];
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching indent report data:', error);
        alert('Error fetching data. Please check your connection and try again.');
      }
      return [];
    }
  };







// Transform API data to show each indent line + additional dispatch/receive items
  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) {
      console.error('API data is not an array:', apiData);
      return [];
    }

    console.log('Transforming API data, count:', apiData.length); // Debug log

    const transformedData = [];
    let index = 0;

    // First: Process each original indent item
    apiData.forEach((item) => {
      try {
        console.log('Processing indent item:', item.IndentID, item.LineNumber, item.ItemID); // Debug log
        
        // Handle KG vs EA quantities properly
        const kgValue = Number(item.KG) || 0;
        const eaValue = Number(item.EA) || 0;
        const qtyValue = Number(item.qty) || 0;
        
        let requiredQtyKg = 0;
        let requiredQtyEA = 0;
        let uom = 'EA'; // default
        
        // If both KG and EA have values, show both and set UOM to KG
        if (kgValue > 0 && eaValue > 0) {
          requiredQtyKg = kgValue;
          requiredQtyEA = eaValue;
          uom = 'KG';
        }
        // If only KG field has a value, use it for KG quantity and set UOM to KG
        else if (kgValue > 0) {
          requiredQtyKg = kgValue;
          requiredQtyEA = 0;
          uom = 'KG';
        }
        // If KG is 0 but EA has a value, use EA quantity and set UOM to EA
        else if (eaValue > 0) {
          requiredQtyKg = 0;
          requiredQtyEA = eaValue;
          uom = 'EA';
        }
        // Fallback to qty field if both KG and EA are 0
        else if (qtyValue > 0) {
          requiredQtyKg = qtyValue;
          requiredQtyEA = 0;
          uom = 'KG';
        }

        // Calculate TR OUT and TR IN quantities for THIS specific ItemID
        let trOutQtyKG = 0, trOutQtyEA = 0, trInQtyKG = 0, trInQtyEA = 0;

        // Get dispatch and receiving information from nested arrays
        let dispatchedDate = 'N/A';
        let dispatchedTime = 'N/A';
        let receivingDate = 'N/A';
        let receivingTime = 'N/A';
        let tatIndentToDispatch = 'N/A';
        let tatDispatchToReceiving = 'N/A';
        let tatOverall = 'N/A';

        // Extract dispatch information
        if (item.Despatch && Array.isArray(item.Despatch) && item.Despatch.length > 0) {
          const firstDispatch = item.Despatch[0];
          dispatchedDate = formatDateOnly(firstDispatch.DispatchedDate || firstDispatch.DispatchedTime);
          dispatchedTime = formatTimeOnly(firstDispatch.DispatchedTime);

          // Calculate TAT from indent creation to dispatch
          tatIndentToDispatch = calculateTAT(item.CalendarDate, firstDispatch.DispatchedTime);

          // Calculate TR OUT quantities - only for items that match this ItemID
          if (firstDispatch.dispatchLine && Array.isArray(firstDispatch.dispatchLine)) {
            firstDispatch.dispatchLine.forEach(dispatchItem => {
              if (dispatchItem.ItemID === item.ItemID) {
                if (uom === 'EA') {
                  trOutQtyEA += Number(dispatchItem.Qty) || 0;
                } else {
                  trOutQtyKG += Number(dispatchItem.Qty) || 0;
                  trOutQtyEA += Number(dispatchItem.AltQty) || 0;
                }
              }
            });
          }

          // Extract receiving information
          if (firstDispatch.receiving && Array.isArray(firstDispatch.receiving) && firstDispatch.receiving.length > 0) {
            const firstReceiving = firstDispatch.receiving[0];
            receivingDate = formatDateOnly(firstReceiving.ReceivingDate || firstReceiving.ReceivingTime);
            receivingTime = formatTimeOnly(firstReceiving.ReceivingTime);

            // Calculate TAT from dispatch to receiving
            tatDispatchToReceiving = calculateTAT(firstDispatch.DispatchedTime, firstReceiving.ReceivingTime);

            // Calculate overall TAT from indent creation to receiving
            tatOverall = calculateTAT(item.CalendarDate, firstReceiving.ReceivingTime);

            // Calculate TR IN quantities - only for items that match this ItemID
            if (firstReceiving.receiveLine && Array.isArray(firstReceiving.receiveLine)) {
              firstReceiving.receiveLine.forEach(receiveItem => {
                if (receiveItem.ItemID === item.ItemID) {
                  if (uom === 'EA') {
                    trInQtyEA += Number(receiveItem.Qty) || 0;
                  } else {
                    trInQtyKG += Number(receiveItem.Qty) || 0;
                    trInQtyEA += Number(receiveItem.AltQty) || 0;
                  }
                }
              });
            }
          }
        }

        const transformedItem = {
          id: index + 1,
          sno: index + 1,
          indentId: `${item.BranchId || ''}-${item.IndentID || ''}`,
          demandForDate: formatDateOnly(item.CalendarDate),
          area: item.AreaName || 'N/A',
          businessDate: formatDateOnly(item.BusinessDate),
          calendarDate: formatDateOnly(item.CalendarDate),
          calendarTime: formatTimeOnly(item.CalendarDate),
          indentType: item.TransTypeID || 'INDENT',
          storeCode: item.BranchId || 'N/A',
          storeName: item.BranchName || 'N/A',
          itemCode: item.ItemID || 'N/A',
          itemName: item.itemname || 'N/A',
          itemGroup: item.ItemFamilyName || 'N/A',
          requiredQtyKg: requiredQtyKg,
          requiredQtyEA: requiredQtyEA,
          uom: uom,
          trOutQtyKG: trOutQtyKG,
          trOutQtyEA: trOutQtyEA,
          trInQtyKG: trInQtyKG,
          trInQtyEA: trInQtyEA,
          toBranchName: item.toBranchName || '',
          lineNumber: item.LineNumber || '',
          status: formatClosedStatus(item.Closed),
          createdDate: formatDateOnly(item.CalendarDate),
          branch: item.BranchId || '',
          altQty: Number(item.AltQty) || 0,
          dispatcherId: item.Dispatcher || 'N/A',
          dispatcherName: item.DispatcherName || 'N/A',
          dispatchedDate,
          dispatchedTime,
          receivingDate,
          receivingTime,
          tatIndentToDispatch,
          tatDispatchToReceiving,
          tatOverall,
          closedRaw: item.Closed,
          closed: formatClosedStatus(item.Closed),
          // Store the raw item for detailed export if needed
          rawItem: item
        };

        console.log('Original indent item:', {
          indentId: transformedItem.indentId,
          lineNumber: transformedItem.lineNumber,
          itemCode: transformedItem.itemCode,
          itemName: transformedItem.itemName,
          requiredQtyEA: transformedItem.requiredQtyEA,
          trOutQtyEA: transformedItem.trOutQtyEA,
          trInQtyEA: transformedItem.trInQtyEA
        }); // Debug log
        
        transformedData.push(transformedItem);
        index++;

      } catch (error) {
        console.error('Error transforming item:', item, error);
        // Continue processing other items even if one fails
      }
    });

    // Second: Add additional rows for dispatch/receive items that don't match any original ItemID
    const processedItemIds = new Set(transformedData.map(item => item.itemCode));
    const allDispatchReceiveItems = new Map(); // Store all dispatch/receive ItemIDs found
    
    // Collect ALL dispatch and receive ItemIDs from all indent records
    apiData.forEach((item) => {
      if (item.Despatch && Array.isArray(item.Despatch)) {
        item.Despatch.forEach(dispatch => {
          // Process dispatch items
          if (dispatch.dispatchLine && Array.isArray(dispatch.dispatchLine)) {
            dispatch.dispatchLine.forEach(dispatchItem => {
              if (!processedItemIds.has(dispatchItem.ItemID)) {
                allDispatchReceiveItems.set(dispatchItem.ItemID, {
                  itemData: dispatchItem,
                  originalItem: item
                });
              }
            });
          }
          
          // Process receiving items to ensure we capture all ItemIDs
          if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
            dispatch.receiving.forEach(receive => {
              if (receive.receiveLine && Array.isArray(receive.receiveLine)) {
                receive.receiveLine.forEach(receiveItem => {
                  if (!processedItemIds.has(receiveItem.ItemID)) {
                    if (!allDispatchReceiveItems.has(receiveItem.ItemID)) {
                      allDispatchReceiveItems.set(receiveItem.ItemID, {
                        itemData: receiveItem,
                        originalItem: item
                      });
                    }
                  }
                });
              }
            });
          }
        });
      }
    });

    console.log('Additional dispatch/receive items found:', allDispatchReceiveItems.size);

    // Create rows for each additional ItemID found
    allDispatchReceiveItems.forEach((itemInfo, itemId) => {
      const originalItem = itemInfo.originalItem;
      const itemData = itemInfo.itemData;
      
      // Get TR OUT and TR IN quantities from the first occurrence only
      let trOutQtyKG = 0, trOutQtyEA = 0, trInQtyKG = 0, trInQtyEA = 0;
      
      // Find the first occurrence of this ItemID in dispatch/receive data
      const firstRecord = originalItem;
      if (firstRecord.Despatch && Array.isArray(firstRecord.Despatch)) {
        firstRecord.Despatch.forEach(dispatch => {
          // Get TR OUT quantities from first occurrence
          if (dispatch.dispatchLine && Array.isArray(dispatch.dispatchLine)) {
            dispatch.dispatchLine.forEach(dispatchItem => {
              if (dispatchItem.ItemID === itemId) {
                trOutQtyEA = Number(dispatchItem.Qty) || 0;
                trOutQtyKG = Number(dispatchItem.AltQty) || 0;
              }
            });
          }
          
          // Get TR IN quantities from first occurrence
          if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
            dispatch.receiving.forEach(receive => {
              if (receive.receiveLine && Array.isArray(receive.receiveLine)) {
                receive.receiveLine.forEach(receiveItem => {
                  if (receiveItem.ItemID === itemId) {
                    trInQtyEA = Number(receiveItem.Qty) || 0;
                    trInQtyKG = Number(receiveItem.AltQty) || 0;
                  }
                });
              }
            });
          }
        });
      }
      
      const additionalRow = {
        id: transformedData.length + 1,
        sno: transformedData.length + 1,
        indentId: `${originalItem.BranchId || ''}-${originalItem.IndentID || ''}`,
        demandForDate: formatDateOnly(originalItem.CalendarDate),
        area: originalItem.AreaName || 'N/A',
        businessDate: formatDateOnly(originalItem.BusinessDate),
        calendarDate: formatDateOnly(originalItem.CalendarDate),
        calendarTime: formatTimeOnly(originalItem.CalendarDate),
        indentType: originalItem.TransTypeID || 'INDENT',
        storeCode: originalItem.BranchId || 'N/A',
        storeName: originalItem.BranchName || 'N/A',
        itemCode: itemId,
        itemName: itemData.ItemName || 'N/A',
        itemGroup: originalItem.ItemFamilyName || 'N/A',
        requiredQtyKg: 0, // Required qty is 0 for additional items
        requiredQtyEA: 0, // Required qty is 0 for additional items
        uom: 'EA', // Default to EA for additional items
        trOutQtyKG: trOutQtyKG,
        trOutQtyEA: trOutQtyEA,
        trInQtyKG: trInQtyKG,
        trInQtyEA: trInQtyEA,
        toBranchName: originalItem.toBranchName || '',
        lineNumber: originalItem.LineNumber || '',
        status: formatClosedStatus(originalItem.Closed),
        createdDate: formatDateOnly(originalItem.CalendarDate),
        branch: originalItem.BranchId || '',
        altQty: 0,
        dispatcherId: originalItem.Dispatcher || 'N/A',
        dispatcherName: originalItem.DispatcherName || 'N/A',
        dispatchedDate: 'N/A',
        dispatchedTime: 'N/A',
        receivingDate: 'N/A',
        receivingTime: 'N/A',
        tatIndentToDispatch: 'N/A',
        tatDispatchToReceiving: 'N/A',
        tatOverall: 'N/A',
        closedRaw: originalItem.Closed,
        closed: formatClosedStatus(originalItem.Closed),
        // Store the raw item for detailed export if needed
        rawItem: originalItem
      };

      console.log('Additional item:', {
        indentId: additionalRow.indentId,
        itemCode: additionalRow.itemCode,
        itemName: additionalRow.itemName,
        requiredQtyEA: additionalRow.requiredQtyEA,
        trOutQtyEA: additionalRow.trOutQtyEA,
        trInQtyEA: additionalRow.trInQtyEA
      }); // Debug log
      
      transformedData.push(additionalRow);
    });

    console.log(`Transformed ${transformedData.length} items total (${apiData.length} original + ${allDispatchReceiveItems.size} additional)`);
    return transformedData;
  };













  // Load areas on component mount - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (userId && authToken) {
  //     fetchAreas();
  //   }
  // }, [userId, authToken]);

  // Load branches when area changes - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (selectedAreaObj && selectedAreaObj.sfAreaId !== 'ALL') {
  //     fetchBranches(selectedAreaObj.sfAreaId);
  //     setSelectedBranchObj(null);
  //   } else {
  //     setBranches([]);
  //     setSelectedBranchObj(null);
  //   }
  // }, [selectedAreaObj]);

  // Initialize data
  useEffect(() => {
    setRawData([]);
    setFilteredData([]);
  }, []);

  // Date range validation - 30 days maximum
  useEffect(() => {
    if (fromDate && toDate) {
      const daysDifference = toDate.diff(fromDate, 'days');
      setIsDateRangeValid(daysDifference <= 31);
    } else {
      setIsDateRangeValid(true);
    }
  }, [fromDate, toDate]);

  // FIXED: Apply filters with better validation and error handling
  const applyFilters = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    if (!isDateRangeValid) {
      alert('Date range cannot exceed 31 days. Please adjust the From Date to be within 31 days of the To Date.');
      return;
    }

    setLoading(true);
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);

    try {
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
      // Use SFAreaId from the API response structure
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      let closedParam = 'Y'; // Default to closed
      
      if (selectedStatus === 'open') {
        closedParam = 'N';
      } else if (selectedStatus === 'closed') {
        closedParam = 'Y';
      }

      console.log('Fetching data with params:', {
        areaId,
        branchId,
        fromDate: fromDate.format('YYYYMMDD'),
        toDate: toDate.format('YYYYMMDD'),
        closedParam
      });

      const data = await fetchIndentReportData(
        areaId,
        branchId,
        fromDate,
        toDate,
        closedParam
      );

      console.log('Received data:', data);
      console.log('Data length:', data.length);

      setRawData(data);
      setFilteredData(data);

      if (data.length === 0) {
        alert('No data found for the selected criteria.');
      } else {
        console.log('Successfully loaded', data.length, 'records');
        console.log('Sample record:', data[0]);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error applying filters:', error);
        alert('Error fetching data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedArea('');
    setSelectedBranch('');
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setBranchType(''); // NEW: Clear segment selection
    setFromDate(dayjs().subtract(1, 'month'));
    setToDate(dayjs());
    setSelectedStatus('');
    setSearchTerm('');
    setRawData([]);
    setFilteredData([]);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setIsDateRangeValid(true); // Reset validation state
  };

  // UPDATED: Export function with new API structure support and TR OUT/TR IN quantities
  const exportToXLSX = () => {
    if (filteredAndSearchedData.length === 0) {
      alert('No data to export.');
      return;
    }


    const exportData = [];

    filteredAndSearchedData.forEach(row => {
      // Calculate TR OUT and TR IN quantities for current row's ItemID
      let trOutQtyKG = 0;
      let trOutQtyEA = 0;
      let trInQtyKG = 0;
      let trInQtyEA = 0;

      // Check if this is an additional item (Required Qty = 0 for both KG and EA)
      const isAdditionalItem = row.requiredQtyKg === 0 && row.requiredQtyEA === 0;
      
      if (isAdditionalItem) {
        // For additional items, calculate TR OUT/TR IN from all dispatch data for this ItemID
        if (row.rawItem && row.rawItem.Despatch && Array.isArray(row.rawItem.Despatch)) {
          row.rawItem.Despatch.forEach(dispatch => {
            // Calculate TR OUT quantities
            if (dispatch.dispatchLine && Array.isArray(dispatch.dispatchLine)) {
              dispatch.dispatchLine.forEach(dispatchItem => {
                if (dispatchItem.ItemID === row.itemCode) {
                  // For additional items, assume EA UOM and put Qty in EA, AltQty in KG
                  trOutQtyEA += Number(dispatchItem.Qty) || 0;
                  trOutQtyKG += Number(dispatchItem.AltQty) || 0;
                }
              });
            }
            
            // Calculate TR IN quantities
            if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
              dispatch.receiving.forEach(receive => {
                if (receive.receiveLine && Array.isArray(receive.receiveLine)) {
                  receive.receiveLine.forEach(receiveItem => {
                    if (receiveItem.ItemID === row.itemCode) {
                      // For additional items, assume EA UOM and put Qty in EA, AltQty in KG
                      trInQtyEA += Number(receiveItem.Qty) || 0;
                      trInQtyKG += Number(receiveItem.AltQty) || 0;
                    }
                  });
                }
              });
            }
          });
        }
      } else {
        // For original indent items, use existing logic
        if (row.rawItem && row.rawItem.Despatch && Array.isArray(row.rawItem.Despatch)) {
          row.rawItem.Despatch.forEach(dispatch => {
            // Calculate TR OUT quantities
            if (dispatch.dispatchLine && Array.isArray(dispatch.dispatchLine)) {
              dispatch.dispatchLine.forEach(dispatchItem => {
                // Only include dispatch items that match the current row's ItemID
                if (dispatchItem.ItemID === row.itemCode) {
                  // For UOM EA, show Qty in EA column, otherwise show in KG column
                  if (row.uom === 'EA') {
                    trOutQtyEA += Number(dispatchItem.Qty) || 0;
                  } else {
                    trOutQtyKG += Number(dispatchItem.Qty) || 0;
                    trOutQtyEA += Number(dispatchItem.AltQty) || 0;
                  }
                }
              });
            }
            
            // Calculate TR IN quantities
            if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
              dispatch.receiving.forEach(receive => {
                if (receive.receiveLine && Array.isArray(receive.receiveLine)) {
                  receive.receiveLine.forEach(receiveItem => {
                    // Only include receive items that match the current row's ItemID
                    if (receiveItem.ItemID === row.itemCode) {
                      // For UOM EA, show Qty in EA column, otherwise show in KG column
                      if (row.uom === 'EA') {
                        trInQtyEA += Number(receiveItem.Qty) || 0;
                      } else {
                        trInQtyKG += Number(receiveItem.Qty) || 0;
                        trInQtyEA += Number(receiveItem.AltQty) || 0;
                      }
                    }
                  });
                }
              });
            }
          });
        }
      }

      // Base row data with original format plus new columns
      const baseData = {
        'Indent ID': row.indentId,
        'Demand date': row.demandForDate,
        'AREA': row.area,
        'Business date': row.businessDate,
        'Calendar Date': row.calendarDate,
        'Calendar Time': row.calendarTime,
        'Dispatched Date': row.dispatchedDate,
        'Dispatched Time': row.dispatchedTime,
        'Receiving Date': row.receivingDate,
        'Receiving Time': row.receivingTime,
        'TAT: Indent to Dispatch': row.tatIndentToDispatch,
        'TAT: Dispatch to Receiving': row.tatDispatchToReceiving,
        'TAT: Overall': row.tatOverall,
        'Indent Type': row.indentType,
        'Indenter ID': row.storeCode,
        'Indenter Name': row.storeName,
        'Dispatcher ID': row.dispatcherId,
        'Dispatcher Name': row.dispatcherName,
        'Item Code': row.itemCode,
        'Item Name': row.itemName,
        'Item Group': row.itemGroup,
        'Required Qty in KG': row.requiredQtyKg,
        'Required Qty in EA': row.requiredQtyEA,
        'UOM': row.uom,
        'TR OUT Qty in KG': trOutQtyKG,
        'TR OUT Qty in EA': trOutQtyEA,
        'TR IN Qty in KG': trInQtyKG,
        'TR IN Qty in EA': trInQtyEA,
        'Closed': row.closed,
        'Alt Qty': row.altQty,
        'Line Number': row.lineNumber
      };

      // Check if we have detailed dispatch/receiving data
      if (row.rawItem && row.rawItem.Despatch && Array.isArray(row.rawItem.Despatch)) {
        // Export each dispatch separately
        row.rawItem.Despatch.forEach((dispatch, dispatchIndex) => {
          const dispatchData = {
            ...baseData,
            'Dispatch #': dispatchIndex + 1,
            'TrOut ID': dispatch.TrOutID || '',
            'Dispatched Date': formatDateOnly(dispatch.DispatchedDate || dispatch.DispatchedTime),
            'Dispatched Time': formatTimeOnly(dispatch.DispatchedTime),
            'TAT: Indent to Dispatch': calculateTAT(row.rawItem.CalendarDate, dispatch.DispatchedTime)
          };

          // If there are receiving records
          if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
            dispatch.receiving.forEach((receive, receiveIndex) => {
              const receiveData = {
                ...dispatchData,
                'Receiving #': receiveIndex + 1,
                'GR ID': receive.GRId || '',
                'Receiving Date': formatDateOnly(receive.ReceivingDate || receive.ReceivingTime),
                'Receiving Time': formatTimeOnly(receive.ReceivingTime),
                'TAT: Dispatch to Receiving': calculateTAT(dispatch.DispatchedTime, receive.ReceivingTime),
                'TAT: Overall': calculateTAT(row.rawItem.CalendarDate, receive.ReceivingTime)
              };

              exportData.push(receiveData);
            });
          } else {
            // No receiving data, just dispatch
            exportData.push(dispatchData);
          }
        });
      } else {
        // No dispatch data, just base data
        exportData.push(baseData);
      }
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Indent Demand Report');
    XLSX.writeFile(wb, 'indent_demand_report.xlsx');
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // FIXED: Search and sort data with better search functionality
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.indentId && item.indentId.toString().toLowerCase().includes(lower)) ||
        (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
        (item.itemGroup && item.itemGroup.toString().toLowerCase().includes(lower)) ||
        (item.itemCode && item.itemCode.toString().toLowerCase().includes(lower)) ||
        (item.storeCode && item.storeCode.toString().toLowerCase().includes(lower)) ||
        (item.storeName && item.storeName.toString().toLowerCase().includes(lower))
      );
    }

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: index + 1
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: index + 1
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header/>
      
      <div className={styles.indent_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* UPDATED: Area Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas} // Disabled until segment is selected or while loading
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* UPDATED: Branch Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => {
                  setSelectedBranchObj(newValue);
                }}
                disabled={!selectedAreaObj || loadingBranches} // Disabled until area is selected or while loading
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={!isDateRangeValid ? datePickerErrorStyle : datePickerStyle}
                  shouldDisableDate={(date) => {
                    // Only disable future dates - allow user to choose any past/present date
                    return date.isAfter(dayjs(), 'day');
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      error: !isDateRangeValid,
                      helperText: !isDateRangeValid ? 'Date range exceeds 31 days' : ''
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  shouldDisableDate={(date) => {
                    // Disable future dates
                    if (date.isAfter(dayjs(), 'day')) return true;
                    
                    if (!fromDate) return false;
                    // Disable dates that are before the fromDate (can't select to date before from date)
                    if (date.isBefore(fromDate, 'day')) return true;
                    // Disable dates that are more than 31 days after the fromDate
                    return date.isAfter(fromDate.add(31, 'days'));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            {/* <div className={styles.indent_filter_group}>
              <select 
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles.indent_filter_select}
              >
                <option value="">Select Status</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div> */}
          </div>

          {/* Date Range Validation Message */}
          {!isDateRangeValid && fromDate && toDate && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #f5c6cb',
              margin: '10px 0',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontWeight: 'bold' }}>⚠️</span>
              <span>
                Date range is {toDate.diff(fromDate, 'days')} days. Maximum allowed is 31 days. 
                Please adjust the From Date to be within 31 days of the To Date.
              </span>
            </div>
          )}

          <div className={styles.indent_filter_actions}>
            <button 
              onClick={applyFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
              disabled={loading || !isDateRangeValid}
              style={{
                opacity: (!isDateRangeValid || loading) ? 0.6 : 1,
                cursor: (!isDateRangeValid || loading) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button 
              onClick={clearFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
            >
              Clear
            </button>
            <button 
              onClick={exportToXLSX} 
              className={`${styles.indent_btn} ${styles.indent_btn_success}`}
            >
              Export XLSX
            </button>
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Item Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.indent_results_summary}>
          <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data}>
              <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th className={styles.indent_non_sortable}>
                      S.No
                    </th>
                    <th onClick={() => handleSort('indentId')} className={styles.indent_sortable}>
                      Indent ID {sortConfig.key === 'indentId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('demandForDate')} className={styles.indent_sortable}>
                      Demand date {sortConfig.key === 'demandForDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('area')} className={styles.indent_sortable}>
                      AREA {sortConfig.key === 'area' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('businessDate')} className={styles.indent_sortable}>
                      Business date {sortConfig.key === 'businessDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('calendarDate')} className={styles.indent_sortable}>
                      Calendar Date {sortConfig.key === 'calendarDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Calendar Time</th>
                    <th>Dispatched Date</th>
                    <th>Dispatched Time</th>
                    <th>Receiving Date</th>
                    <th>Receiving Time</th>
                    <th>TAT: Indent to Dispatch</th>
                    <th>TAT: Dispatch to Receiving</th>
                    <th>TAT: Overall</th>
                    <th onClick={() => handleSort('indentType')} className={styles.indent_sortable}>
                      Indent Type {sortConfig.key === 'indentType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('storeCode')} className={styles.indent_sortable}>
                      Indenter ID {sortConfig.key === 'storeCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Indenter Name</th>
                    <th>Dispatcher ID</th>
                    <th>Dispatcher Name</th>
                    <th onClick={() => handleSort('itemCode')} className={styles.indent_sortable}>
                      Item Code {sortConfig.key === 'itemCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Item Name</th>
                    <th>Item Group</th>
                    <th onClick={() => handleSort('requiredQtyKg')} className={styles.indent_sortable}>
                      Required Qty in KG {sortConfig.key === 'requiredQtyKg' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('requiredQtyEA')} className={styles.indent_sortable}>
                      Required Qty in EA {sortConfig.key === 'requiredQtyEA' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>UOM</th>
                    <th onClick={() => handleSort('closed')} className={styles.indent_sortable}>
                      Closed {sortConfig.key === 'closed' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sno}</td>
                      <td className={styles.indent_ticket_id}>{row.indentId}</td>
                      <td>{row.demandForDate}</td>
                      <td className={styles.indent_capitalize}>{row.area}</td>
                      <td>{row.businessDate}</td>
                      <td>{row.calendarDate}</td>
                      <td>{row.calendarTime}</td>
                      <td>{row.dispatchedDate}</td>
                      <td>{row.dispatchedTime}</td>
                      <td>{row.receivingDate}</td>
                      <td>{row.receivingTime}</td>
                      <td className={styles.indent_amount}>{row.tatIndentToDispatch}</td>
                      <td className={styles.indent_amount}>{row.tatDispatchToReceiving}</td>
                      <td className={styles.indent_amount}>{row.tatOverall}</td>
                      <td>{row.indentType}</td>
                      <td className={styles.indent_ticket_id}>{row.storeCode}</td>
                      <td>{row.storeName}</td>
                      <td className={styles.indent_ticket_id}>{row.dispatcherId}</td>
                      <td>{row.dispatcherName}</td>
                      <td className={styles.indent_ticket_id}>{row.itemCode}</td>
                      <td>{row.itemName}</td>
                      <td>{row.itemGroup}</td>
                      <td className={styles.indent_amount}>
                        {Number(row.requiredQtyKg).toFixed(3)}
                      </td>
                      <td className={styles.indent_amount}>{row.requiredQtyEA}</td>
                      <td>{row.uom}</td>
                      <td>{row.closed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.indent_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default IndentDemandReport;













































// import React, { useState, useEffect, useMemo } from 'react';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
// import { Server } from 'lucide-react';
// import dayjs from 'dayjs';
// import * as XLSX from 'xlsx';
// import styles from './IndentDemandReport.module.css';
// import Header from '../../Header';
// import { useNavigate } from 'react-router-dom';
// import { local_signage, local_url } from '../../Const';

// export const IndentDemandReport = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
  
//   // State for filters
//   const [selectedArea, setSelectedArea] = useState('');
//   const [selectedBranch, setSelectedBranch] = useState('');
//   const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
//   const [toDate, setToDate] = useState(dayjs());
//   const [selectedStatus, setSelectedStatus] = useState('');
//   const [searchTerm, setSearchTerm] = useState('');
  
//   // Date validation state
//   const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  
//   // NEW: Add State for Segments, Areas, Branches, and BranchType
//   const [segments, setSegments] = useState([]);
//   const [branchType, setBranchType] = useState('');
//   const [areas, setAreas] = useState([]);
//   const [branches, setBranches] = useState([]);
//   const [selectedAreaObj, setSelectedAreaObj] = useState(null);
//   const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
//   // State for table
//   const [loading, setLoading] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [rowsPerPage, setRowsPerPage] = useState(500);
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
//   const [rawData, setRawData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);

//   // Loading states for dropdowns
//   const [loadingAreas, setLoadingAreas] = useState(false);
//   const [loadingBranches, setLoadingBranches] = useState(false);

//   // Session timeout dialog state
//   const [showSessionDialog, setShowSessionDialog] = useState(false);

//   // Get user data from localStorage
//   const userId = localStorage.getItem("userId");
//   const authToken = localStorage.getItem("AuthorizationToken");

//   // Helper functions to handle date/time without UTC conversion
//   const parseLocalDateTime = (dateString) => {
//     if (!dateString) return new Date();
    
//     try {
//       // Remove the 'Z' and treat as local time (IST) instead of UTC
//       const localDateString = dateString.replace('Z', '');
//       const date = new Date(localDateString);
//       return isNaN(date.getTime()) ? new Date() : date;
//     } catch (error) {
//       console.error('Error parsing date:', dateString, error);
//       return new Date();
//     }
//   };

//   const formatDateOnly = (dateString) => {
//     if (!dateString) return '';
    
//     try {
//       const date = parseLocalDateTime(dateString);
//       return date.toLocaleDateString('en-IN', {
//         day: '2-digit',
//         month: '2-digit',
//         year: 'numeric'
//       });
//     } catch (error) {
//       console.error('Error formatting date only:', dateString, error);
//       return '';
//     }
//   };

//   const formatTimeOnly = (dateString) => {
//     if (!dateString) return '';
    
//     try {
//       const date = parseLocalDateTime(dateString);
//       return date.toLocaleTimeString('en-IN', {
//         hour: '2-digit',
//         minute: '2-digit',
//         hour12: true
//       });
//     } catch (error) {
//       console.error('Error formatting time only:', dateString, error);
//       return '';
//     }
//   };

//   // Helper function to format closed status
//   const formatClosedStatus = (closedValue) => {
//     if (closedValue === true) return 'Yes';
//     if (closedValue === false || closedValue === null || closedValue === undefined) return 'No';
//     return 'N'; // For any other value
//   };

//   // Calculate TAT (Turn Around Time) in hours and minutes
//   const calculateTAT = (startDateTime, endDateTime) => {
//     if (!startDateTime || !endDateTime) return 'N/A';
    
//     try {
//       const start = parseLocalDateTime(startDateTime);
//       const end = parseLocalDateTime(endDateTime);
      
//       if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
      
//       const diffMs = end.getTime() - start.getTime();
      
//       if (diffMs < 0) return 'N/A'; // End time before start time
      
//       const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
//       const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
//       if (diffHours === 0) {
//         return `${diffMinutes}m`;
//       } else {
//         return `${diffHours}h ${diffMinutes}m`;
//       }
//     } catch (error) {
//       console.error('Error calculating TAT:', startDateTime, endDateTime, error);
//       return 'N/A';
//     }
//   };

//   // Function to clear all localStorage and navigate to login
//   const handleSessionTimeout = () => {
//     localStorage.clear();
//     setShowSessionDialog(false);
//     alert('Session Timeout! Please login again.');
//     navigate('/Login', { replace: true });
//   };

//   // Generic API call wrapper with 401 handling
//   const apiCall = async (url, options = {}) => {
//     const controller = new AbortController();
//     const timeout = setTimeout(() => controller.abort(), 60000);

//     try {
//       const response = await fetch(url, {
//         ...options,
//         signal: controller.signal,
//       });

//       clearTimeout(timeout);

//       if (response.status === 401 || response.status === 403) {
//         setShowSessionDialog(true);
//         throw new Error('Session expired');
//       }

//       return response;
//     } catch (error) {
//       clearTimeout(timeout);
//       if (error.name === 'AbortError') {
//         alert('Request timed out. Please try again.');
//       }
//       if (error.message === 'Session expired') {
//         throw error;
//       }
//       console.error('API call error:', error);
//       throw error;
//     }
//   };

//   // Session Timeout Dialog Component
//   const SessionTimeoutDialog = () => (
//     <Dialog
//       open={showSessionDialog}
//       onClose={() => {}}
//       disableEscapeKeyDown
//       maxWidth="sm"
//       fullWidth
//     >
//       <DialogTitle 
//         sx={{ 
//           backgroundColor: '#f44336', 
//           color: 'white',
//           textAlign: 'center',
//           fontWeight: 'bold'
//         }}
//       >
//         Session Timeout
//       </DialogTitle>
//       <DialogContent sx={{ textAlign: 'center', py: 3 }}>
//         <Typography variant="h6" gutterBottom>
//           Your session has expired
//         </Typography>
//         <Typography variant="body1" color="text.secondary">
//           Please login again to continue using the application.
//         </Typography>
//       </DialogContent>
//       <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
//         <Button
//           onClick={handleSessionTimeout}
//           variant="contained"
//           color="primary"
//           size="large"
//           sx={{ minWidth: 120 }}
//         >
//           Login Again
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );

//   // Date picker styling
//   const datePickerStyle = {
//     '& .MuiOutlinedInput-root': {
//       height: '35px',
//       fontSize: '14px',
//       backgroundColor: '#ffffff',
//       '& fieldset': {
//         borderColor: '#dee2e6',
//       },
//       '&:hover fieldset': {
//         borderColor: '#adb5bd',
//       },
//       '&.Mui-focused fieldset': {
//         borderColor: '#667eea',
//       },
//     },
//     '& .MuiInputLabel-root': {
//       fontSize: '14px',
//       color: '#6c757d',
//     },
//   };

//   // Date picker styling with error (red border)
//   const datePickerErrorStyle = {
//     '& .MuiOutlinedInput-root': {
//       height: '35px',
//       fontSize: '14px',
//       backgroundColor: '#ffffff',
//       '& fieldset': {
//         borderColor: '#dc3545',
//         borderWidth: '2px',
//       },
//       '&:hover fieldset': {
//         borderColor: '#c82333',
//         borderWidth: '2px',
//       },
//       '&.Mui-focused fieldset': {
//         borderColor: '#dc3545',
//         borderWidth: '2px',
//       },
//     },
//     '& .MuiInputLabel-root': {
//       fontSize: '14px',
//       color: '#dc3545',
//     },
//   };

//   // Loader Component
//   const FullPageLoader = () => (
//     <Box
//       sx={{
//         position: 'absolute',
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         justifyContent: 'center',
//         backgroundColor: 'rgba(248, 250, 252, 0.85)',
//         backdropFilter: 'blur(8px)',
//         zIndex: 9999,
//       }}
//     >
//       <Box
//         sx={{
//           width: 100,
//           height: 100,
//           position: 'relative',
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           mb: 3,
//         }}
//       >
//         <Box
//           sx={{
//             position: 'absolute',
//             width: '100%',
//             height: '100%',
//             borderRadius: '50%',
//             border: '3px solid transparent',
//             borderTopColor: theme.palette.primary.main,
//             animation: 'spin 1s linear infinite',
//           }}
//         />
//         <Box
//           sx={{
//             position: 'absolute',
//             width: '80%',
//             height: '80%',
//             borderRadius: '50%',
//             border: '3px solid transparent',
//             borderTopColor: theme.palette.secondary?.main || '#1976d2',
//             animation: 'spin 1.5s linear infinite',
//           }}
//         />
//         <Box
//           sx={{
//             position: 'absolute',
//             width: '60%',
//             height: '60%',
//             borderRadius: '50%',
//             border: '3px solid transparent',
//             borderTopColor: theme.palette.info?.main || '#0288d1',
//             animation: 'spin 2s linear infinite',
//           }}
//         />

//         <Box
//           sx={{
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             backgroundColor: 'white',
//             borderRadius: '50%',
//             width: '40%',
//             height: '40%',
//             boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
//           }}
//         >
//           <Server
//             size={20}
//             color={theme.palette.primary.main}
//           />
//         </Box>
//       </Box>

//       <Typography
//         variant="h5"
//         color="primary"
//         mb={1}
//         sx={{ fontWeight: 600 }}
//       >
//         Loading System Data
//       </Typography>

//       <Typography
//         variant="body2"
//         color="text.secondary"
//         align="center"
//         sx={{
//           maxWidth: '300px',
//           animation: 'pulse 1.5s infinite ease-in-out',
//         }}
//       >
//         Please wait while we load indent demand data...
//       </Typography>

//       <Box
//         sx={{
//           mt: 4,
//           height: 4,
//           width: 220,
//           borderRadius: 2,
//           overflow: 'hidden',
//           backgroundColor: alpha(theme.palette.primary.main, 0.1),
//         }}
//       >
//         <Box
//           sx={{
//             height: '100%',
//             width: '100%',
//             backgroundImage: `linear-gradient(90deg, 
//               rgba(255,255,255,0), 
//               ${theme.palette.primary.main}, 
//               rgba(255,255,255,0))`,
//             backgroundSize: '1000px 100%',
//             animation: 'shimmer 2s infinite',
//           }}
//         />
//       </Box>
//     </Box>
//   );

//   const statusOptions = [
//     { value: 'open', label: 'Open' },
//     { value: 'closed', label: 'Closed' }
//   ];

//   // Fetch Segments on Mount
//   useEffect(() => {
//     const fetchSegments = async () => {
//       try {
//         const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
//           headers: {
//             'Authorization': `Bearer ${authToken}`,
//             'Content-Type': 'application/json',
//           },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           setSegments(data.filter(segment => segment.deleted === "N"));
//         }
//       } catch (error) {
//         if (error.message !== 'Session expired') {
//           console.error('Error fetching business segments:', error);
//         }
//       }
//     };

//     if (authToken && userId) fetchSegments();
//   }, [authToken, userId]);

//   // Fetch Areas When BranchType Changes
//   useEffect(() => {
//     const fetchAreasBySegment = async () => {
//       if (!branchType) return;

//       setLoadingAreas(true);
//       try {
//         const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
//           headers: {
//             'Authorization': `Bearer ${authToken}`,
//             'Content-Type': 'application/json',
//           },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           // Add ALL option and set the areas
//           const areasWithAll = [
//             { SFAreaId: 'ALL', SFAreaName: 'ALL' },
//             ...data
//           ];
//           setAreas(areasWithAll);
//           setSelectedAreaObj(null);
//           setBranches([]); // Clear branches when areas change
//           setSelectedBranchObj(null);
//         }
//       } catch (error) {
//         if (error.message !== 'Session expired') {
//           console.error('Error fetching areas by segment:', error);
//         }
//         setAreas([]);
//         setBranches([]);
//         setSelectedAreaObj(null);
//         setSelectedBranchObj(null);
//       } finally {
//         setLoadingAreas(false);
//       }
//     };

//     if (authToken && branchType) fetchAreasBySegment();
//   }, [branchType, authToken]);

//   // Fetch Branches When Area or BranchType Changes
//   useEffect(() => {
//     const fetchBranchList = async () => {
//       if (!branchType || !selectedAreaObj) return;

//       setLoadingBranches(true);
//       try {
//         // Use SFAreaId from the API response structure
//         const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
//         const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
//         const response = await apiCall(url, {
//           headers: {
//             'Authorization': `Bearer ${authToken}`,
//             'Content-Type': 'application/json',
//           },
//         });
//         if (response.ok) {
//           const data = await response.json();
//           setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
//           setSelectedBranchObj(null);
//         }
//       } catch (error) {
//         if (error.message !== 'Session expired') {
//           console.error('Error fetching branch list:', error);
//         }
//         setBranches([]);
//       } finally {
//         setLoadingBranches(false);
//       }
//     };

//     if (authToken && branchType && selectedAreaObj) fetchBranchList();
//   }, [branchType, selectedAreaObj, authToken]);

//   // Fetch indent report data with proper error handling
//   const fetchIndentReportData = async (areaId, branchId, fromDate, toDate, closedParam = 'AL') => {
//     try {
//       const startDate = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
//       const endDate = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');

//       const response = await apiCall(
//         `${local_signage}/api/v1/indentreport?BizSegmentId=${branchType || 'ALL'}&AreaID=${areaId}&BranchID=${branchId}&ItemID=ALL&StartDate=${startDate}&EndDate=${endDate}&closed=AL`,
//         {
//           method: 'GET',
//           headers: {
//             'Authorization': `Bearer ${authToken}`,
//             'Content-Type': 'application/json'
//           }
//         }
//       );

//       if (response.ok) {
//         const data = await response.json();
//         console.log('Raw API Response:', data); // Debug log
        
//         // Handle the new API response structure with DashBoard wrapper
//         let actualData = data;
//         if (data && data.DashBoard && Array.isArray(data.DashBoard)) {
//           actualData = data.DashBoard;
//         } else if (Array.isArray(data)) {
//           actualData = data;
//         } else {
//           console.error('Unexpected API response format:', data);
//           actualData = [];
//         }
        
//         console.log('Processed data for transformation:', actualData); // Debug log
//         return transformApiData(actualData);
//       } else {
//         const errorText = await response.text();
//         console.error('Failed to fetch indent report data:', response.status, errorText);
//         alert(`Failed to fetch data: ${response.status} - ${errorText}`);
//         return [];
//       }
//     } catch (error) {
//       if (error.message !== 'Session expired') {
//         console.error('Error fetching indent report data:', error);
//         alert('Error fetching data. Please check your connection and try again.');
//       }
//       return [];
//     }
//   };

//   // Transform API data to handle different items in dispatch/receive vs indent
//   const transformApiData = (apiData) => {
//     if (!Array.isArray(apiData)) {
//       console.error('API data is not an array:', apiData);
//       return [];
//     }

//     console.log('Transforming API data, count:', apiData.length);

//     const transformedData = [];
//     let index = 0;

//     // Group items by IndentID to process them together
//     const indentGroups = {};
    
//     apiData.forEach((item) => {
//       const indentId = `${item.BranchId || ''}-${item.IndentID || ''}`;
//       if (!indentGroups[indentId]) {
//         indentGroups[indentId] = {
//           indentInfo: {
//             indentId: indentId,
//             demandForDate: formatDateOnly(item.CalendarDate),
//             area: item.AreaName || 'N/A',
//             businessDate: formatDateOnly(item.BusinessDate),
//             calendarDate: formatDateOnly(item.CalendarDate),
//             calendarTime: formatTimeOnly(item.CalendarDate),
//             indentType: item.TransTypeID || 'INDENT',
//             storeCode: item.BranchId || 'N/A',
//             storeName: item.BranchName || 'N/A',
//             itemGroup: item.ItemFamilyName || 'N/A',
//             toBranchName: item.toBranchName || '',
//             lineNumber: item.LineNumber || '',
//             status: formatClosedStatus(item.Closed),
//             createdDate: formatDateOnly(item.CalendarDate),
//             branch: item.BranchId || '',
//             altQty: Number(item.AltQty) || 0,
//             dispatcherId: item.Dispatcher || 'N/A',
//             dispatcherName: item.DispatcherName || 'N/A',
//             closedRaw: item.Closed,
//             closed: formatClosedStatus(item.Closed),
//             rawItem: item
//           },
//           indentItems: [],
//           allItems: []
//         };
//       }
//       indentGroups[indentId].indentItems.push(item);
//     });

//     // Process each indent group
//     Object.values(indentGroups).forEach((group) => {
//       const { indentInfo, indentItems } = group;
      
//       // Collect all unique ItemIDs from this indent group
//       const allUniqueItems = new Map();

//       // First, add all original indent items
//      // First add all indent items (with required qty, no TR OUT/IN yet)
// indentItems.forEach((item) => {
//   const itemId = item.ItemID;
//   if (itemId) {
//     transformedData.push({
//       id: index + 1,
//       sno: index + 1,
//       indentId: indentInfo.indentId,
//       itemCode: itemId,
//       itemName: item.itemname || 'N/A',
//       requiredQtyKg: Number(item.KG) || 0,
//       requiredQtyEA: Number(item.EA) || Number(item.qty) || 0,
//       uom: item.KG > 0 ? 'KG' : 'EA',
//       trOutQtyKG: 0,
//       trOutQtyEA: 0,
//       trInQtyKG: 0,
//       trInQtyEA: 0,
//       isOriginalIndentItem: true,
//       ...indentInfo
//     });
//     index++;
//   }
// });

// // Now add dispatch/receive items separately (with TR OUT/IN qty, required=0)
// indentItems.forEach((indentRecord) => {
//   if (indentRecord.Despatch && Array.isArray(indentRecord.Despatch)) {
//     indentRecord.Despatch.forEach(dispatch => {
//       // Dispatch items
//       if (dispatch.dispatchLine) {
//         dispatch.dispatchLine.forEach(dispatchItem => {
//           transformedData.push({
//             id: index + 1,
//             sno: index + 1,
//             indentId: indentInfo.indentId,
//             itemCode: dispatchItem.ItemID,
//             itemName: dispatchItem.ItemName,
//             requiredQtyKg: 0,
//             requiredQtyEA: 0,
//             trOutQtyKG: Number(dispatchItem.AltQty) || 0,
//             trOutQtyEA: Number(dispatchItem.Qty) || 0,
//             trInQtyKG: 0,
//             trInQtyEA: 0,
//             isOriginalIndentItem: false,
//             ...indentInfo
//           });
//           index++;
//         });
//       }

//       // Receiving items
//       if (dispatch.receiving) {
//         dispatch.receiving.forEach(receive => {
//           if (receive.receiveLine) {
//             receive.receiveLine.forEach(receiveItem => {
//               transformedData.push({
//                 id: index + 1,
//                 sno: index + 1,
//                 indentId: indentInfo.indentId,
//                 itemCode: receiveItem.ItemID,
//                 itemName: receiveItem.ItemName,
//                 requiredQtyKg: 0,
//                 requiredQtyEA: 0,
//                 trOutQtyKG: 0,
//                 trOutQtyEA: 0,
//                 trInQtyKG: Number(receiveItem.AltQty) || 0,
//                 trInQtyEA: Number(receiveItem.Qty) || 0,
//                 isOriginalIndentItem: false,
//                 ...indentInfo
//               });
//               index++;
//             });
//           }
//         });
//       }
//     });
//   }
// });


//       // Now create a row for each unique ItemID and calculate TR OUT/IN quantities
//       allUniqueItems.forEach((itemInfo, itemId) => {
//         // Calculate TR OUT and TR IN quantities for this specific ItemID across this indent group
//         let trOutQtyKG = 0, trOutQtyEA = 0, trInQtyKG = 0, trInQtyEA = 0;
//         let dispatchedDate = 'N/A';
//         let dispatchedTime = 'N/A';
//         let receivingDate = 'N/A';
//         let receivingTime = 'N/A';
//         let tatIndentToDispatch = 'N/A';
//         let tatDispatchToReceiving = 'N/A';
//         let tatOverall = 'N/A';

//         indentItems.forEach((indentRecord) => {
//           if (indentRecord.Despatch && Array.isArray(indentRecord.Despatch)) {
//             indentRecord.Despatch.forEach(dispatch => {
//               // For the first dispatch found, capture timing info
//               if (dispatchedDate === 'N/A') {
//                 dispatchedDate = formatDateOnly(dispatch.DispatchedDate || dispatch.DispatchedTime);
//                 dispatchedTime = formatTimeOnly(dispatch.DispatchedTime);
//                 tatIndentToDispatch = calculateTAT(indentRecord.CalendarDate, dispatch.DispatchedTime);
//               }

//               // Calculate TR OUT quantities for this ItemID
//               if (dispatch.dispatchLine && Array.isArray(dispatch.dispatchLine)) {
//                 dispatch.dispatchLine.forEach(dispatchItem => {
//                   if (dispatchItem.ItemID === itemId) {
//                     trOutQtyEA += Number(dispatchItem.Qty) || 0;
//                     trOutQtyKG += Number(dispatchItem.AltQty) || 0;
//                   }
//                 });
//               }
              
//               // Calculate TR IN quantities and timing for this ItemID
//               if (dispatch.receiving && Array.isArray(dispatch.receiving)) {
//                 dispatch.receiving.forEach(receive => {
//                   // For the first receive found, capture timing info
//                   if (receivingDate === 'N/A') {
//                     receivingDate = formatDateOnly(receive.ReceivingDate || receive.ReceivingTime);
//                     receivingTime = formatTimeOnly(receive.ReceivingTime);
//                     tatDispatchToReceiving = calculateTAT(dispatch.DispatchedTime, receive.ReceivingTime);
//                     tatOverall = calculateTAT(indentRecord.CalendarDate, receive.ReceivingTime);
//                   }

//                   if (receive.receiveLine && Array.isArray(receive.receiveLine)) {
//   receive.receiveLine.forEach(receiveItem => {
//     if (receiveItem.ItemID === itemId) {
//       trInQtyEA += Number(receiveItem.Qty) || 0;
//       trInQtyKG += Number(receiveItem.AltQty) || 0;
//     }
//   });
// }
//                 });
//               }
//             });
//           }
//         });

//         const transformedItem = {
//           id: index + 1,
//           sno: index + 1,
//           indentId: indentInfo.indentId,
//           demandForDate: indentInfo.demandForDate,
//           area: indentInfo.area,
//           businessDate: indentInfo.businessDate,
//           calendarDate: indentInfo.calendarDate,
//           calendarTime: indentInfo.calendarTime,
//           indentType: indentInfo.indentType,
//           storeCode: indentInfo.storeCode,
//           storeName: indentInfo.storeName,
//           itemCode: itemId,
//           itemName: itemInfo.itemName,
//           itemGroup: indentInfo.itemGroup,
//           requiredQtyKg: itemInfo.requiredQtyKg,
//           requiredQtyEA: itemInfo.requiredQtyEA,
//           uom: itemInfo.uom,
//           trOutQtyKG: trOutQtyKG,
//           trOutQtyEA: trOutQtyEA,
//           trInQtyKG: trInQtyKG,
//           trInQtyEA: trInQtyEA,
//           toBranchName: indentInfo.toBranchName,
//           lineNumber: indentInfo.lineNumber,
//           status: indentInfo.status,
//           createdDate: indentInfo.createdDate,
//           branch: indentInfo.branch,
//           altQty: indentInfo.altQty,
//           dispatcherId: indentInfo.dispatcherId,
//           dispatcherName: indentInfo.dispatcherName,
//           dispatchedDate,
//           dispatchedTime,
//           receivingDate,
//           receivingTime,
//           tatIndentToDispatch,
//           tatDispatchToReceiving,
//           tatOverall,
//           closedRaw: indentInfo.closedRaw,
//           closed: indentInfo.closed,
//           rawItem: indentInfo.rawItem,
//           isOriginalIndentItem: itemInfo.isOriginalIndentItem
//         };

//         console.log('Transformed item:', {
//           indentId: transformedItem.indentId,
//           itemCode: transformedItem.itemCode,
//           itemName: transformedItem.itemName,
//           requiredQtyEA: transformedItem.requiredQtyEA,
//           trOutQtyEA: transformedItem.trOutQtyEA,
//           trInQtyEA: transformedItem.trInQtyEA,
//           isOriginal: transformedItem.isOriginalIndentItem
//         });
        
//         transformedData.push(transformedItem);
//         index++;
//       });
//     });

//     console.log(`Transformed ${transformedData.length} items total`);
//     return transformedData;
//   };

//   // Initialize data
//   useEffect(() => {
//     setRawData([]);
//     setFilteredData([]);
//   }, []);

//   // Date range validation - 30 days maximum
//   useEffect(() => {
//     if (fromDate && toDate) {
//       const daysDifference = toDate.diff(fromDate, 'days');
//       setIsDateRangeValid(daysDifference <= 31);
//     } else {
//       setIsDateRangeValid(true);
//     }
//   }, [fromDate, toDate]);

//   // Apply filters with better validation and error handling
//   const applyFilters = async () => {
//     if (!selectedAreaObj) {
//       alert('Please select an Area to fetch data.');
//       return;
//     }

//     if (!fromDate || !toDate) {
//       alert('Please select both From Date and To Date.');
//       return;
//     }

//     if (fromDate.isAfter(toDate)) {
//       alert('From Date cannot be after To Date.');
//       return;
//     }

//     if (!isDateRangeValid) {
//       alert('Date range cannot exceed 31 days. Please adjust the From Date to be within 31 days of the To Date.');
//       return;
//     }

//     setLoading(true);
//     setSortConfig({ key: null, direction: 'asc' });
//     setCurrentPage(1);

//     try {
//       const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
//       // Use SFAreaId from the API response structure
//       const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
//       let closedParam = 'Y'; // Default to closed
      
//       if (selectedStatus === 'open') {
//         closedParam = 'N';
//       } else if (selectedStatus === 'closed') {
//         closedParam = 'Y';
//       }

//       console.log('Fetching data with params:', {
//         areaId,
//         branchId,
//         fromDate: fromDate.format('YYYYMMDD'),
//         toDate: toDate.format('YYYYMMDD'),
//         closedParam
//       });

//       const data = await fetchIndentReportData(
//         areaId,
//         branchId,
//         fromDate,
//         toDate,
//         closedParam
//       );

//       console.log('Received data:', data);
//       console.log('Data length:', data.length);

//       setRawData(data);
//       setFilteredData(data);

//       if (data.length === 0) {
//         alert('No data found for the selected criteria.');
//       } else {
//         console.log('Successfully loaded', data.length, 'records');
//         console.log('Sample record:', data[0]);
//       }
//     } catch (error) {
//       if (error.message !== 'Session expired') {
//         console.error('Error applying filters:', error);
//         alert('Error fetching data. Please try again.');
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Clear filters
//   const clearFilters = () => {
//     setSelectedArea('');
//     setSelectedBranch('');
//     setSelectedAreaObj(null);
//     setSelectedBranchObj(null);
//     setBranchType(''); // Clear segment selection
//     setFromDate(dayjs().subtract(1, 'month'));
//     setToDate(dayjs());
//     setSelectedStatus('');
//     setSearchTerm('');
//     setRawData([]);
//     setFilteredData([]);
//     setCurrentPage(1);
//     setSortConfig({ key: null, direction: 'asc' });
//     setIsDateRangeValid(true); // Reset validation state
//   };

//   // Export function simplified since TR OUT/IN quantities are now calculated in transformApiData
//   const exportToXLSX = () => {
//     if (filteredAndSearchedData.length === 0) {
//       alert('No data to export.');
//       return;
//     }

//     const exportData = filteredAndSearchedData.map(row => ({
//       'Indent ID': row.indentId,
//       'Demand date': row.demandForDate,
//       'AREA': row.area,
//       'Business date': row.businessDate,
//       'Calendar Date': row.calendarDate,
//       'Calendar Time': row.calendarTime,
//       'Dispatched Date': row.dispatchedDate,
//       'Dispatched Time': row.dispatchedTime,
//       'Receiving Date': row.receivingDate,
//       'Receiving Time': row.receivingTime,
//       'TAT: Indent to Dispatch': row.tatIndentToDispatch,
//       'TAT: Dispatch to Receiving': row.tatDispatchToReceiving,
//       'TAT: Overall': row.tatOverall,
//       'Indent Type': row.indentType,
//       'Indenter ID': row.storeCode,
//       'Indenter Name': row.storeName,
//       'Dispatcher ID': row.dispatcherId,
//       'Dispatcher Name': row.dispatcherName,
//       'Item Code': row.itemCode,
//       'Item Name': row.itemName,
//       'Item Group': row.itemGroup,
//       'Required Qty in KG': row.requiredQtyKg,
//       'Required Qty in EA': row.requiredQtyEA,
//       'UOM': row.uom,
//       'TR OUT Qty in KG': row.trOutQtyKG || 0,
//       'TR OUT Qty in EA': row.trOutQtyEA || 0,
//       'TR IN Qty in KG': row.trInQtyKG || 0,
//       'TR IN Qty in EA': row.trInQtyEA || 0,
//       'Closed': row.closed,
//       'Alt Qty': row.altQty,
//       'Line Number': row.lineNumber
//     }));

//     const ws = XLSX.utils.json_to_sheet(exportData);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Indent Demand Report');
//     XLSX.writeFile(wb, 'indent_demand_report.xlsx');
//   };

//   // Sorting function
//   const handleSort = (key) => {
//     let direction = 'asc';
//     if (sortConfig.key === key && sortConfig.direction === 'asc') {
//       direction = 'desc';
//     }
//     setSortConfig({ key, direction });
//   };

//   // Search and sort data with better search functionality
//   const filteredAndSearchedData = useMemo(() => {
//     let data = filteredData;

//     if (searchTerm) {
//       const lower = searchTerm.toLowerCase().trim();
//       data = data.filter(item =>
//         (item.indentId && item.indentId.toString().toLowerCase().includes(lower)) ||
//         (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
//         (item.itemGroup && item.itemGroup.toString().toLowerCase().includes(lower)) ||
//         (item.itemCode && item.itemCode.toString().toLowerCase().includes(lower)) ||
//         (item.storeCode && item.storeCode.toString().toLowerCase().includes(lower)) ||
//         (item.storeName && item.storeName.toString().toLowerCase().includes(lower))
//       );
//     }

//     if (!sortConfig.key) {
//       return data.map((item, index) => ({
//         ...item,
//         sno: index + 1
//       }));
//     }

//     return [...data].sort((a, b) => {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];

//       // Handle numeric sorting
//       if (typeof aValue === 'number' && typeof bValue === 'number') {
//         return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
//       }

//       // Handle string sorting
//       const aStr = String(aValue || '').toLowerCase();
//       const bStr = String(bValue || '').toLowerCase();
      
//       if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
//       return 0;
//     }).map((item, index) => ({
//       ...item,
//       sno: index + 1
//     }));
//   }, [filteredData, sortConfig, searchTerm]);

//   // Auto-redirect to first page when search results change
//   useEffect(() => {
//     if (searchTerm || filteredAndSearchedData.length > 0) {
//       const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
//       if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
//         setCurrentPage(1);
//       }
//     }
//   }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

//   // Reset to first page when search term changes
//   useEffect(() => {
//     if (searchTerm) {
//       setCurrentPage(1);
//     }
//   }, [searchTerm]);

//   // Pagination
//   const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
//   const startIndex = (currentPage - 1) * rowsPerPage;
//   const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

//   return (
//     <>
//       <Header/>
      
//       <div className={styles.indent_dashboard}>
//         {/* Session Timeout Dialog */}
//         <SessionTimeoutDialog />

//         {/* Filters Section */}
//         <div className={styles.indent_filters_section}>
//           <div className={styles.indent_filters_grid}>
//             {/* Segment Dropdown */}
//             <div className={styles.indent_filter_group}>
//               <select
//                 value={branchType}
//                 onChange={e => setBranchType(e.target.value)}
//                 className={styles.indent_filter_select}
//                 style={{
//                   height: '35px',
//                   fontSize: '14px',
//                   backgroundColor: '#ffffff',
//                   border: '1px solid #dee2e6',
//                   borderRadius: '4px',
//                   padding: '0 8px',
//                   minWidth: '200px',
//                   color: '#495057',
//                   outline: 'none',
//                   transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
//                 }}
//                 onFocus={(e) => {
//                   e.target.style.borderColor = '#667eea';
//                   e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
//                 }}
//                 onBlur={(e) => {
//                   e.target.style.borderColor = '#dee2e6';
//                   e.target.style.boxShadow = 'none';
//                 }}
//                 onMouseEnter={(e) => {
//                   if (e.target !== document.activeElement) {
//                     e.target.style.borderColor = '#adb5bd';
//                   }
//                 }}
//                 onMouseLeave={(e) => {
//                   if (e.target !== document.activeElement) {
//                     e.target.style.borderColor = '#dee2e6';
//                   }
//                 }}
//               >
//                 <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
//                 {segments.map(segment => (
//                   <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
//                     {segment.businessSegmentName}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             {/* Area Dropdown */}
//             <div className={styles.indent_filter_group}>
//               <Autocomplete
//                 options={areas}
//                 getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
//                 value={selectedAreaObj}
//                 onChange={(event, newValue) => {
//                   setSelectedAreaObj(newValue);
//                 }}
//                 disabled={!branchType || loadingAreas}
//                 loading={loadingAreas}
//                 renderInput={(params) => (
//                   <TextField
//                     {...params}
//                     label={loadingAreas ? "Loading Areas..." : "Select Area"}
//                     variant="outlined"
//                     size="small"
//                     sx={{
//                       '& .MuiOutlinedInput-root': {
//                         height: '35px',
//                         fontSize: '14px',
//                         backgroundColor: '#ffffff',
//                       }
//                     }}
//                     InputProps={{
//                       ...params.InputProps,
//                       endAdornment: (
//                         <>
//                           {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
//                             <Box
//                               sx={{
//                                 width: 16,
//                                 height: 16,
//                                 border: '2px solid #e0e0e0',
//                                 borderTop: '2px solid #1976d2',
//                                 borderRadius: '50%',
//                                 animation: 'spin 1s linear infinite'
//                               }}
//                             />
//                           </Box> : null}
//                           {params.InputProps.endAdornment}
//                         </>
//                       ),
//                     }}
//                   />
//                 )}
//                 sx={{ minWidth: 200 }}
//               />
//             </div>

//             {/* Branch Dropdown */}
//             <div className={styles.indent_filter_group}>
//               <Autocomplete
//                 options={branches}
//                 getOptionLabel={(option) => option.branchName || ''}
//                 value={selectedBranchObj}
//                 onChange={(event, newValue) => {
//                   setSelectedBranchObj(newValue);
//                 }}
//                 disabled={!selectedAreaObj || loadingBranches}
//                 loading={loadingBranches}
//                 renderInput={(params) => (
//                   <TextField
//                     {...params}
//                     label={loadingBranches ? "Loading Branches..." : "Select Branch"}
//                     variant="outlined"
//                     size="small"
//                     sx={{
//                       '& .MuiOutlinedInput-root': {
//                         height: '35px',
//                         fontSize: '14px',
//                         backgroundColor: '#ffffff',
//                       }
//                     }}
//                     InputProps={{
//                       ...params.InputProps,
//                       endAdornment: (
//                         <>
//                           {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
//                             <Box
//                               sx={{
//                                 width: 16,
//                                 height: 16,
//                                 border: '2px solid #e0e0e0',
//                                 borderTop: '2px solid #1976d2',
//                                 borderRadius: '50%',
//                                 animation: 'spin 1s linear infinite'
//                               }}
//                             />
//                           </Box> : null}
//                           {params.InputProps.endAdornment}
//                         </>
//                       ),
//                     }}
//                   />
//                 )}
//                 sx={{ minWidth: 200 }}
//               />
//             </div>

//             <div className={styles.indent_filter_group}>
//               <LocalizationProvider dateAdapter={AdapterDayjs}>
//                 <DatePicker
//                   label="From Date"
//                   value={fromDate}
//                   onChange={(newValue) => setFromDate(newValue)}
//                   format="DD-MM-YYYY"
//                   sx={!isDateRangeValid ? datePickerErrorStyle : datePickerStyle}
//                   shouldDisableDate={(date) => {
//                     // Only disable future dates - allow user to choose any past/present date
//                     return date.isAfter(dayjs(), 'day');
//                   }}
//                   slotProps={{
//                     textField: {
//                       size: "small",
//                       variant: "outlined",
//                       error: !isDateRangeValid,
//                       helperText: !isDateRangeValid ? 'Date range exceeds 31 days' : ''
//                     }
//                   }}
//                 />
//               </LocalizationProvider>
//             </div>

//             <div className={styles.indent_filter_group}>
//               <LocalizationProvider dateAdapter={AdapterDayjs}>
//                 <DatePicker
//                   label="To Date"
//                   value={toDate}
//                   onChange={(newValue) => setToDate(newValue)}
//                   format="DD-MM-YYYY"
//                   sx={datePickerStyle}
//                   shouldDisableDate={(date) => {
//                     // Disable future dates
//                     if (date.isAfter(dayjs(), 'day')) return true;
                    
//                     if (!fromDate) return false;
//                     // Disable dates that are before the fromDate (can't select to date before from date)
//                     if (date.isBefore(fromDate, 'day')) return true;
//                     // Disable dates that are more than 31 days after the fromDate
//                     return date.isAfter(fromDate.add(31, 'days'));
//                   }}
//                   slotProps={{
//                     textField: {
//                       size: "small",
//                       variant: "outlined"
//                     }
//                   }}
//                 />
//               </LocalizationProvider>
//             </div>
//           </div>

//           {/* Date Range Validation Message */}
//           {!isDateRangeValid && fromDate && toDate && (
//             <div style={{
//               backgroundColor: '#f8d7da',
//               color: '#721c24',
//               padding: '8px 12px',
//               borderRadius: '4px',
//               border: '1px solid #f5c6cb',
//               margin: '10px 0',
//               fontSize: '14px',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '8px'
//             }}>
//               <span style={{ fontWeight: 'bold' }}>⚠️</span>
//               <span>
//                 Date range is {toDate.diff(fromDate, 'days')} days. Maximum allowed is 31 days. 
//                 Please adjust the From Date to be within 31 days of the To Date.
//               </span>
//             </div>
//           )}

//           <div className={styles.indent_filter_actions}>
//             <button 
//               onClick={applyFilters} 
//               className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
//               disabled={loading || !isDateRangeValid}
//               style={{
//                 opacity: (!isDateRangeValid || loading) ? 0.6 : 1,
//                 cursor: (!isDateRangeValid || loading) ? 'not-allowed' : 'pointer'
//               }}
//             >
//               {loading ? 'Loading...' : 'Apply'}
//             </button>
//             <button 
//               onClick={clearFilters} 
//               className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
//             >
//               Clear
//             </button>
//             <button 
//               onClick={exportToXLSX} 
//               className={`${styles.indent_btn} ${styles.indent_btn_success}`}
//             >
//               Export XLSX
//             </button>
//             <div className={styles.indent_search_group}>
//               <input
//                 type="text"
//                 placeholder="Search by Item Name..."
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className={styles.indent_search_input}
//               />
//             </div>
//           </div>
//         </div>

//         {/* Results Summary */}
//         <div className={styles.indent_results_summary}>
//           <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
//           <div className={styles.indent_rows_per_page}>
//             <label>Rows:</label>
//             <select 
//               value={rowsPerPage}
//               onChange={(e) => {
//                 setRowsPerPage(Number(e.target.value));
//                 setCurrentPage(1);
//               }}
//               className={styles.indent_rows_select}
//             >
//               <option value={100}>100</option>
//               <option value={200}>200</option>
//               <option value={500}>500</option>
//             </select>
//           </div>
//         </div>

//         {/* Data Table Section */}
//         <div className={styles.indent_table_section}>
//           {loading ? (
//             <FullPageLoader />
//           ) : filteredAndSearchedData.length === 0 ? (
//             <div className={styles.indent_no_data}>
//               <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
//             </div>
//           ) : (
//             <div className={styles.indent_table_container}>
//               <table className={styles.indent_data_table}>
//                 <thead>
//                   <tr>
//                     <th className={styles.indent_non_sortable}>
//                       S.No
//                     </th>
//                     <th onClick={() => handleSort('indentId')} className={styles.indent_sortable}>
//                       Indent ID {sortConfig.key === 'indentId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('demandForDate')} className={styles.indent_sortable}>
//                       Demand date {sortConfig.key === 'demandForDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('area')} className={styles.indent_sortable}>
//                       AREA {sortConfig.key === 'area' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('businessDate')} className={styles.indent_sortable}>
//                       Business date {sortConfig.key === 'businessDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('calendarDate')} className={styles.indent_sortable}>
//                       Calendar Date {sortConfig.key === 'calendarDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th>Calendar Time</th>
//                     <th>Dispatched Date</th>
//                     <th>Dispatched Time</th>
//                     <th>Receiving Date</th>
//                     <th>Receiving Time</th>
//                     <th>TAT: Indent to Dispatch</th>
//                     <th>TAT: Dispatch to Receiving</th>
//                     <th>TAT: Overall</th>
//                     <th onClick={() => handleSort('indentType')} className={styles.indent_sortable}>
//                       Indent Type {sortConfig.key === 'indentType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('storeCode')} className={styles.indent_sortable}>
//                       Indenter ID {sortConfig.key === 'storeCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th>Indenter Name</th>
//                     <th>Dispatcher ID</th>
//                     <th>Dispatcher Name</th>
//                     <th onClick={() => handleSort('itemCode')} className={styles.indent_sortable}>
//                       Item Code {sortConfig.key === 'itemCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th>Item Name</th>
//                     <th>Item Group</th>
//                     <th onClick={() => handleSort('requiredQtyKg')} className={styles.indent_sortable}>
//                       Required Qty in KG {sortConfig.key === 'requiredQtyKg' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('requiredQtyEA')} className={styles.indent_sortable}>
//                       Required Qty in EA {sortConfig.key === 'requiredQtyEA' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th>UOM</th>
//                     <th onClick={() => handleSort('trOutQtyKG')} className={styles.indent_sortable}>
//                       TR OUT Qty in KG {sortConfig.key === 'trOutQtyKG' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('trOutQtyEA')} className={styles.indent_sortable}>
//                       TR OUT Qty in EA {sortConfig.key === 'trOutQtyEA' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('trInQtyKG')} className={styles.indent_sortable}>
//                       TR IN Qty in KG {sortConfig.key === 'trInQtyKG' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('trInQtyEA')} className={styles.indent_sortable}>
//                       TR IN Qty in EA {sortConfig.key === 'trInQtyEA' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                     <th onClick={() => handleSort('closed')} className={styles.indent_sortable}>
//                       Closed {sortConfig.key === 'closed' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {paginatedData.map((row) => (
//                     <tr key={row.id}>
//                       <td>{row.sno}</td>
//                       <td className={styles.indent_ticket_id}>{row.indentId}</td>
//                       <td>{row.demandForDate}</td>
//                       <td className={styles.indent_capitalize}>{row.area}</td>
//                       <td>{row.businessDate}</td>
//                       <td>{row.calendarDate}</td>
//                       <td>{row.calendarTime}</td>
//                       <td>{row.dispatchedDate}</td>
//                       <td>{row.dispatchedTime}</td>
//                       <td>{row.receivingDate}</td>
//                       <td>{row.receivingTime}</td>
//                       <td className={styles.indent_amount}>{row.tatIndentToDispatch}</td>
//                       <td className={styles.indent_amount}>{row.tatDispatchToReceiving}</td>
//                       <td className={styles.indent_amount}>{row.tatOverall}</td>
//                       <td>{row.indentType}</td>
//                       <td className={styles.indent_ticket_id}>{row.storeCode}</td>
//                       <td>{row.storeName}</td>
//                       <td className={styles.indent_ticket_id}>{row.dispatcherId}</td>
//                       <td>{row.dispatcherName}</td>
//                       <td className={styles.indent_ticket_id}>{row.itemCode}</td>
//                       <td>{row.itemName}</td>
//                       <td>{row.itemGroup}</td>
//                       <td className={styles.indent_amount}>
//                         {Number(row.requiredQtyKg).toFixed(3)}
//                       </td>
//                       <td className={styles.indent_amount}>{row.requiredQtyEA}</td>
//                       <td>{row.uom}</td>
//                       <td className={styles.indent_amount}>
//                         {Number(row.trOutQtyKG || 0).toFixed(3)}
//                       </td>
//                       <td className={styles.indent_amount}>{row.trOutQtyEA || 0}</td>
//                       <td className={styles.indent_amount}>
//                         {Number(row.trInQtyKG || 0).toFixed(3)}
//                       </td>
//                       <td className={styles.indent_amount}>{row.trInQtyEA || 0}</td>
//                       <td>{row.closed}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         {/* Pagination */}
//         {filteredAndSearchedData.length > 0 && !loading && (
//           <div className={styles.indent_pagination}>
//             {/* First Page Button */}
//             <button 
//               onClick={() => setCurrentPage(1)}
//               disabled={currentPage === 1}
//               className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
//             >
//               First
//             </button>
            
//             {/* Previous Button */}
//             <button 
//               onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//               className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
//             >
//               Prev
//             </button>
            
//             {/* Page Numbers */}
//             <div className={styles.indent_page_numbers}>
//               {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//                 let page;
//                 if (totalPages <= 5) {
//                   page = i + 1;
//                 } else if (currentPage <= 3) {
//                   page = i + 1;
//                 } else if (currentPage >= totalPages - 2) {
//                   page = totalPages - 4 + i;
//                 } else {
//                   page = currentPage - 2 + i;
//                 }
//                 return (
//                   <button
//                     key={page}
//                     onClick={() => setCurrentPage(page)}
//                     className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
//                   >
//                     {page}
//                   </button>
//                 );
//               })}
//             </div>
            
//             {/* Next Button */}
//             <button 
//               onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//               disabled={currentPage === totalPages}
//               className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
//             >
//               Next
//             </button>
            
//             {/* Last Page Button */}
//             <button 
//               onClick={() => setCurrentPage(totalPages)}
//               disabled={currentPage === totalPages}
//               className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
//             >
//               Last
//             </button>
//           </div>
//         )}
//       </div>

//       {/* Add CSS animations */}
//       <style jsx>{`
//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }
        
//         @keyframes pulse {
//           0%, 100% { opacity: 1; }
//           50% { opacity: 0.7; }
//         }
        
//         @keyframes shimmer {
//           0% { transform: translateX(-100%); }
//           100% { transform: translateX(100%); }
//         }
//       `}</style>
//     </>
//   );
// };

// export default IndentDemandReport;